import React from 'react';
import styled, { keyframes, css } from 'styled-components';

export type ProgressIndicatorType =
    | 'bar'
    | 'circle'
    | 'dots'
    | 'steps'
    | 'liquid'
    | 'gradient-ring'
    | 'pulse-bar'
    | 'wave';

interface ProgressIndicatorsProps {
    currentIndex: number;
    total: number;
    type: ProgressIndicatorType;
    variant?: 'default' | 'thin' | 'thick' | 'minimal' | 'bold';
    showText?: boolean;
    textPosition?: 'above' | 'below' | 'inside' | 'none';
    maxWidth?: string;
    size?: string;
    color?: 'purple' | 'blue' | 'green' | 'orange' | 'red' | 'gradient';
    animated?: boolean;
}

// Animations
const pulse = keyframes`
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
`;

const wave = keyframes`
    0%, 60%, 100% { transform: initial; }
    30% { transform: translateY(-15px); }
`;

const liquidFlow = keyframes`
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
`;

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

// Main Container
const ProgressContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 25px;
`;

const ProgressText = styled.div<{
    position: 'above' | 'below' | 'inside';
    color: string;
    isInside?: boolean;
}>`
    font-size: ${(props) => (props.isInside ? '12px' : '14px')};
    color: ${(props) => props.color};
    margin: ${(props) => {
        if (props.isInside) return '0';
        return props.position === 'above' ? '0 0 8px 0' : '8px 0 0 0';
    }};
    font-weight: 500;
    ${(props) =>
        props.isInside &&
        `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10;
        color: white;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    `}
`;

// Bar Progress
const ProgressBarContainer = styled.div<{ maxWidth: string }>`
    width: 100%;
    max-width: ${(props) => props.maxWidth};
    margin: 0 auto;
    position: relative;
`;

const ProgressBarTrack = styled.div<{
    height: string;
    variant: string;
    color: string;
}>`
    width: 100%;
    height: ${(props) => props.height};
    background: ${(props) => `${props.color}20`};
    border-radius: ${(props) => {
        const h = parseInt(props.height, 10);
        return props.variant === 'minimal' ? '2px' : `${h / 2}px`;
    }};
    overflow: hidden;
    box-shadow: ${(props) =>
        props.variant === 'thick' ? 'inset 0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
    position: relative;
`;

const ProgressFill = styled.div<{
    progress: number;
    variant: string;
    color: string;
    animated: boolean;
}>`
    height: 100%;
    width: ${(props) => props.progress}%;
    background: ${(props) => props.color};
    border-radius: inherit;
    transition: width 0.3s ease;
    position: relative;

    ${(props) =>
        props.animated &&
        css`
            &::after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(
                    90deg,
                    transparent,
                    rgba(255, 255, 255, 0.3),
                    transparent
                );
                animation: ${liquidFlow} 2s infinite;
            }
        `}
`;

// Pulse Bar
const PulseBarTrack = styled(ProgressBarTrack)<{ animated: boolean }>`
    ${(props) =>
        props.animated &&
        css`
            animation: ${pulse} 2s infinite;
        `}
`;

// Wave Progress
const WaveContainer = styled.div<{ maxWidth: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: ${(props) => props.maxWidth};
    gap: 4px;
    height: 40px;
`;

const WaveDot = styled.div<{
    isActive: boolean;
    color: string;
    animated: boolean;
    delay: number;
}>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => (props.isActive ? props.color : `${props.color}30`)};
    transition: all 0.3s ease;

    ${(props) =>
        props.animated &&
        props.isActive &&
        css`
            animation: ${wave} 1.4s infinite ease-in-out;
            animation-delay: ${props.delay}s;
        `}
`;

// Circle Progress
const CircleContainer = styled.div<{ size: string }>`
    position: relative;
    width: ${(props) => props.size};
    height: ${(props) => props.size};
`;

const CircleSvg = styled.svg<{ animated: boolean }>`
    transform: rotate(-90deg);
    ${(props) =>
        props.animated &&
        css`
            animation: ${spin} 2s linear infinite;
        `}
`;

const CircleTrack = styled.circle<{ color: string }>`
    fill: none;
    stroke: ${(props) => `${props.color}20`};
    stroke-width: 8;
`;

const CircleFill = styled.circle<{
    progress: number;
    color: string;
    circumference: number;
}>`
    fill: none;
    stroke: ${(props) => props.color};
    stroke-width: 8;
    stroke-linecap: round;
    stroke-dasharray: ${(props) => props.circumference};
    stroke-dashoffset: ${(props) =>
        props.circumference - (props.progress / 100) * props.circumference};
    transition: stroke-dashoffset 0.3s ease;
`;

// Dots Progress
const DotsContainer = styled.div<{ maxWidth: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: ${(props) => props.maxWidth};
    gap: 8px;
`;

const Dot = styled.div<{
    isActive: boolean;
    isCompleted: boolean;
    color: string;
    variant: string;
    animated: boolean;
}>`
    width: ${(props) => {
        if (props.variant === 'thin') return '6px';
        if (props.variant === 'thick') return '16px';
        return '12px';
    }};
    height: ${(props) => {
        if (props.variant === 'thin') return '6px';
        if (props.variant === 'thick') return '16px';
        return '12px';
    }};
    border-radius: 50%;
    background: ${(props) => {
        if (props.isCompleted) return props.color;
        if (props.isActive) return `${props.color}80`;
        return `${props.color}30`;
    }};
    transition: all 0.3s ease;

    ${(props) =>
        props.animated &&
        props.isActive &&
        css`
            animation: ${pulse} 1.5s infinite;
        `}

    ${(props) =>
        props.isActive &&
        css`
            transform: scale(1.2);
        `}
`;

// Steps Progress
const StepsContainer = styled.div<{ maxWidth: string }>`
    display: flex;
    align-items: center;
    max-width: ${(props) => props.maxWidth};
    width: 100%;
`;

const Step = styled.div<{
    isActive: boolean;
    isCompleted: boolean;
    color: string;
    animated: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${(props) => {
        if (props.isCompleted) return props.color;
        if (props.isActive) return `${props.color}80`;
        return `${props.color}30`;
    }};
    color: white;
    font-size: 12px;
    font-weight: bold;
    transition: all 0.3s ease;

    ${(props) =>
        props.animated &&
        props.isActive &&
        css`
            animation: ${pulse} 2s infinite;
        `}
`;

const StepConnector = styled.div<{
    isCompleted: boolean;
    color: string;
    isLast: boolean;
}>`
    flex: 1;
    height: 3px;
    background: ${(props) => (props.isCompleted ? props.color : `${props.color}30`)};
    margin: 0 8px;
    transition: background 0.3s ease;
    display: ${(props) => (props.isLast ? 'none' : 'block')};
`;

// Gradient Ring
const GradientRingContainer = styled.div<{ size: string }>`
    position: relative;
    width: ${(props) => props.size};
    height: ${(props) => props.size};
`;

const GradientRingSvg = styled.svg<{ animated: boolean }>`
    ${(props) =>
        props.animated &&
        css`
            animation: ${spin} 3s linear infinite;
        `}
`;

const ProgressIndicators: React.FC<ProgressIndicatorsProps> = ({
    currentIndex,
    total,
    type,
    variant = 'default',
    showText = true,
    textPosition = 'above',
    maxWidth = '400px',
    size = '100px',
    color = 'purple',
    animated = false,
}) => {
    const progress = total > 0 ? ((currentIndex + 1) / total) * 100 : 0;

    const getColorValue = () => {
        switch (color) {
            case 'blue':
                return '#3b82f6';
            case 'green':
                return '#10b981';
            case 'orange':
                return '#f59e0b';
            case 'red':
                return '#ef4444';
            case 'gradient':
                return 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)';
            default:
                return '#7c3aed';
        }
    };

    const getHeightByVariant = () => {
        switch (variant) {
            case 'thin':
                return '4px';
            case 'thick':
                return '16px';
            case 'minimal':
                return '2px';
            case 'bold':
                return '20px';
            default:
                return '8px';
        }
    };

    const colorValue = getColorValue();
    const height = getHeightByVariant();

    const renderProgressText = (position: 'above' | 'below' | 'inside') => {
        if (!showText || textPosition === 'none' || textPosition !== position) return null;

        return (
            <ProgressText position={position} color={colorValue} isInside={position === 'inside'}>
                {position === 'inside'
                    ? `${Math.round(progress)}%`
                    : `Progress: ${currentIndex + 1} of ${total}`}
            </ProgressText>
        );
    };

    const renderBarProgress = () => (
        <ProgressBarContainer maxWidth={maxWidth}>
            {renderProgressText('inside')}
            <ProgressBarTrack height={height} variant={variant} color={colorValue}>
                <ProgressFill
                    progress={progress}
                    variant={variant}
                    color={colorValue}
                    animated={animated}
                />
            </ProgressBarTrack>
        </ProgressBarContainer>
    );

    const renderPulseBar = () => (
        <ProgressBarContainer maxWidth={maxWidth}>
            {renderProgressText('inside')}
            <PulseBarTrack height={height} variant={variant} color={colorValue} animated={animated}>
                <ProgressFill
                    progress={progress}
                    variant={variant}
                    color={colorValue}
                    animated={false}
                />
            </PulseBarTrack>
        </ProgressBarContainer>
    );

    const renderWaveProgress = () => (
        <WaveContainer maxWidth={maxWidth}>
            {Array.from({ length: total }, (_, index) => (
                <WaveDot
                    key={index}
                    isActive={index <= currentIndex}
                    color={colorValue}
                    animated={animated}
                    delay={index * 0.1}
                />
            ))}
        </WaveContainer>
    );

    const renderCircleProgress = () => {
        const radius = 40;
        const circumference = 2 * Math.PI * radius;

        return (
            <CircleContainer size={size}>
                {renderProgressText('inside')}
                <CircleSvg width={size} height={size} viewBox="0 0 100 100" animated={animated}>
                    <CircleTrack cx="50" cy="50" r={radius} color={colorValue} />
                    <CircleFill
                        cx="50"
                        cy="50"
                        r={radius}
                        progress={progress}
                        color={colorValue}
                        circumference={circumference}
                    />
                </CircleSvg>
            </CircleContainer>
        );
    };

    const renderDotsProgress = () => (
        <DotsContainer maxWidth={maxWidth}>
            {Array.from({ length: total }, (_, index) => (
                <Dot
                    key={index}
                    isActive={index === currentIndex}
                    isCompleted={index < currentIndex}
                    color={colorValue}
                    variant={variant}
                    animated={animated}
                />
            ))}
        </DotsContainer>
    );

    const renderStepsProgress = () => (
        <StepsContainer maxWidth={maxWidth}>
            {Array.from({ length: total }, (_, index) => (
                <React.Fragment key={index}>
                    <Step
                        isActive={index === currentIndex}
                        isCompleted={index < currentIndex}
                        color={colorValue}
                        animated={animated}
                    >
                        {index + 1}
                    </Step>
                    <StepConnector
                        isCompleted={index < currentIndex}
                        color={colorValue}
                        isLast={index === total - 1}
                    />
                </React.Fragment>
            ))}
        </StepsContainer>
    );

    const renderLiquidProgress = () => (
        <ProgressBarContainer maxWidth={maxWidth}>
            {renderProgressText('inside')}
            <ProgressBarTrack height="20px" variant={variant} color={colorValue}>
                <ProgressFill progress={progress} variant={variant} color={colorValue} animated />
            </ProgressBarTrack>
        </ProgressBarContainer>
    );

    const renderGradientRing = () => {
        const radius = 35;
        const circumference = 2 * Math.PI * radius;

        return (
            <GradientRingContainer size={size}>
                {renderProgressText('inside')}
                <GradientRingSvg width={size} height={size} viewBox="0 0 80 80" animated={animated}>
                    <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#667eea" />
                            <stop offset="50%" stopColor="#764ba2" />
                            <stop offset="100%" stopColor="#7c3aed" />
                        </linearGradient>
                    </defs>
                    <CircleTrack cx="40" cy="40" r={radius} color={colorValue} />
                    <CircleFill
                        cx="40"
                        cy="40"
                        r={radius}
                        progress={progress}
                        color="url(#gradient)"
                        circumference={circumference}
                    />
                </GradientRingSvg>
            </GradientRingContainer>
        );
    };

    const renderProgress = () => {
        switch (type) {
            case 'circle':
                return renderCircleProgress();
            case 'dots':
                return renderDotsProgress();
            case 'steps':
                return renderStepsProgress();
            case 'liquid':
                return renderLiquidProgress();
            case 'gradient-ring':
                return renderGradientRing();
            case 'pulse-bar':
                return renderPulseBar();
            case 'wave':
                return renderWaveProgress();
            default:
                return renderBarProgress();
        }
    };

    return (
        <ProgressContainer>
            {renderProgressText('above')}
            {renderProgress()}
            {renderProgressText('below')}
        </ProgressContainer>
    );
};

ProgressIndicators.defaultProps = {
    variant: 'default',
    showText: true,
    textPosition: 'above',
    maxWidth: '400px',
    size: '100px',
    color: 'purple',
    animated: false,
};

export default ProgressIndicators;
